import { LayoutDashboard, LogIn, Menu, X } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { useMe } from "@/lib/session";
import { Logo } from "./logo";
import { PublicCta, PublicFooter } from "./public-footer";

export interface PublicNavItem {
  href: string;
  label: string;
}

/** Frame for pages anyone can read before signing in: a fixed header, the page, an invitation, the footer. */
export function PublicLayout({ nav = [], cta = true, children }: { nav?: readonly PublicNavItem[]; cta?: boolean; children: ReactNode }) {
  const { data: me } = useMe();
  const [open, setOpen] = useState(false);
  // The full-screen menu locks the page behind it and closes on Escape.
  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);
  const account = me ? (
    <Link to="/"><LayoutDashboard />管理画面へ</Link>
  ) : (
    <Link to="/login"><LogIn />ログイン</Link>
  );
  return (
    <div className="public-layout flex min-h-dvh flex-col">
      <header className="sticky top-0 z-30 border-b border-line bg-surface/92 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-[1440px] items-center justify-between gap-4 px-4 sm:px-8 lg:h-20">
          <Logo />
          {nav.length > 0 && (
            <nav aria-label="ページ内の案内" className="hidden items-center gap-6 lg:flex xl:gap-8">
              {nav.map((n) => (
                <a key={n.href} href={n.href} className="public-nav-link text-[14px] font-bold whitespace-nowrap text-ink-2">
                  {n.label}
                </a>
              ))}
            </nav>
          )}
          <div className="flex items-center gap-2">
            <Button asChild size="sm" variant="outline" className="hidden sm:inline-flex">{account}</Button>
            {nav.length > 0 && (
              <button type="button" aria-label={open ? "メニューを閉じる" : "メニュー"} aria-expanded={open} onClick={() => setOpen((v) => !v)} className="flex size-10 items-center justify-center rounded-full border border-line text-ink lg:hidden">
                {open ? <X className="size-5" /> : <Menu className="size-5" />}
              </button>
            )}
          </div>
        </div>
      </header>
      <AnimatePresence>
        {open && (
          <motion.div key="menu" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }} className="public-menu fixed inset-0 z-20 flex flex-col items-center justify-center gap-7 bg-ground/95 pt-16 backdrop-blur-md lg:hidden">
            {nav.map((n, i) => (
              <motion.a key={n.href} href={n.href} onClick={() => setOpen(false)} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.08 + i * 0.06, ease: [0.22, 1, 0.36, 1] }} className="font-display text-[1.35rem] font-bold text-ink">
                {n.label}
              </motion.a>
            ))}
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.08 + nav.length * 0.06 }} className="mt-2">
              <Button asChild size="lg" variant="outline" onClick={() => setOpen(false)}>{account}</Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <main className="flex-1">{children}</main>
      {cta && <PublicCta />}
      <PublicFooter nav={nav} />
    </div>
  );
}
